import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRemote } from '../../Utils/Utils';
import Page, { PageContentBodyGrid } from '../Shared/Page/Page';
import PageGridItem from '../Shared/Page/PageGridItem';
import FlexWrapper from '../FlexWrapper';
import Divider from '../../components/Divider';
import LoadingIcon from '../../components/LoadingIcon';
import Button from '../../components/Buttons/Button';
import { Download } from 'react-feather';
import { isPdfGuide, splitStepTitle, parseStepText } from './helpGuideUtils';


const HelpPage = () => {
	const { guideId } = useParams();
	const remote = useRemote();
	const videoRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [videoLoading, setVideoLoading] = useState(true);
	const [videoError, setVideoError] = useState(null);
	const [images, setImages] = useState(null);
	const [loadingImages, setLoadingImages] = useState(true);
	const [video, setVideo] = useState(null);
	const [loadingVideo, setLoadingVideo] = useState(true);

	const [s3VideoUrl, setS3VideoUrl] = useState(null);
	const [loadingS3Video, setLoadingS3Video] = useState(true);

	// null | 'notFound' (404: unknown or inaccessible guide) | 'error' (any other failure)
	const [loadError, setLoadError] = useState(null);

	const [downloading, setDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState(null);
	const downloadingRef = useRef(false);

	useEffect(() => {
		// Ignore responses for a guide the user has already navigated away from
		let stale = false;
		if (guideId) {
			setLoadError(null);
			setVideoError(null);
			setLoadingVideo(true);
			setLoadingS3Video(true);
			setLoadingImages(true);
			setImages(null);
			setS3VideoUrl(null);
			setDownloadError(null);
			remote.get(`crm/help/videos/${guideId}`).then(v => {
				if (stale) return;
				setVideo(v);
				setLoadingVideo(false);
				if (isPdfGuide(v.url)) {
					// PDF guides have no player; the download link is fetched on click
					setLoadingS3Video(false);
				} else {
					remote.get(`documents/${v.url}`).then(resp => {
						if (stale) return;
						setS3VideoUrl(resp.url);
						setLoadingS3Video(false);
					}).catch(() => {
						if (stale) return;
						setVideoError('Error loading video');
						setLoadingS3Video(false);
					});
				}
				if (v.transcripts && v.transcripts.length > 0) {
					Promise.all(v.transcripts.map(t => remote.get(`documents/${t.folder}/${t.object}`))).then(response => {
						if (stale) return;
						setImages(response.map(res => res.url));
						setLoadingImages(false);
					}).catch(() => {
						// Steps still render without their screenshots
						if (stale) return;
						setImages(null);
						setLoadingImages(false);
					});
				} else {
					setLoadingImages(false);
				}
			}).catch(status => {
				// RemoteV2 rejects with the HTTP status. 404 = unknown guide, or one this user cannot access
				// (e.g. an old /help/{id} bookmark); anything else is a load failure, not a missing guide.
				if (stale) return;
				setLoadError(status === 404 ? 'notFound' : 'error');
				setLoadingVideo(false);
			});
		}
		return () => { stale = true; };
	}, [guideId])

	useEffect(() => {
		if (!videoRef.current) return;

		const video = videoRef.current;
		const handleLoadStart = () => setVideoLoading(true);
		const handleLoadedData = () => setVideoLoading(false);
		const handleError = () => {
			setVideoError('Error loading video');
			setVideoLoading(false);
		};

		video.addEventListener('loadstart', handleLoadStart);
		video.addEventListener('loadeddata', handleLoadedData);
		video.addEventListener('error', handleError);
		video.addEventListener('ended', () => setIsPlaying(false));

		return () => {
			video.removeEventListener('loadstart', handleLoadStart);
			video.removeEventListener('loadeddata', handleLoadedData);
			video.removeEventListener('error', handleError);
			video.removeEventListener('ended', () => setIsPlaying(false));
		};
	}, [videoRef]);

	if (!guideId) {
		return (
			<Page noBanner>
				<PageContentBodyGrid id="error-message" style={{ gridTemplateRows: "1fr" }}>
					<PageGridItem style={{ height: "100%" }} colSpan="12">
						<FlexWrapper align="center" justify="center" direction="column" fullWidth style={{ height: "100%" }}>
							<p className='p-message'>Select a guide in the sidebar</p>
						</FlexWrapper>
					</PageGridItem>
				</PageContentBodyGrid>
			</Page>
		);
	}

	if (loadingVideo) {
		return (
			<Page noBanner>
				<PageContentBodyGrid id="error-message" style={{ gridTemplateRows: "1fr" }}>
					<PageGridItem style={{ height: "100%" }} colSpan="12">
						<FlexWrapper align="center" justify="center" direction="column" fullWidth style={{ height: "100%" }}>
							<LoadingIcon />
						</FlexWrapper>
					</PageGridItem>
				</PageContentBodyGrid>
			</Page>
		);
	}

	if (loadError) {
		return (
			<Page noBanner>
				<PageContentBodyGrid id="error-message" style={{ gridTemplateRows: "1fr" }}>
					<PageGridItem style={{ height: "100%" }} colSpan="12">
						<FlexWrapper align="center" justify="center" direction="column" fullWidth style={{ height: "100%" }}>
							<h1 className='mb-m'>{loadError === 'notFound' ? 'Guide not found' : 'Guide could not be loaded'}</h1>
							<p className='p-message'>{loadError === 'notFound' ? 'Select a guide in the sidebar' : 'Please try again later'}</p>
						</FlexWrapper>
					</PageGridItem>
				</PageContentBodyGrid>
			</Page>
		);
	}

	function renderMarkdownText(text) {
		// This regex matches **bold** or __bold__ and captures the bold part
		const regex = /(\*\*([^*]+)\*\*|__([^_]+)__)/g;
		const parts = [];
		let lastIndex = 0;
		let match;
		let key = 0;

		while ((match = regex.exec(text)) !== null) {
			// Push text before the bold part
			if (match.index > lastIndex) {
				parts.push(text.slice(lastIndex, match.index));
			}
			// Push the bold part
			parts.push(<strong key={key++}>{match[2] || match[3]}</strong>);
			lastIndex = regex.lastIndex;
		}
		// Push any remaining text after the last bold
		if (lastIndex < text.length) {
			parts.push(text.slice(lastIndex));
		}
		return parts;
	}

	const pdfGuide = isPdfGuide(video?.url);

	// The presigned S3 url expires after 10 seconds, so it is fetched on click rather than on load.
	// The tab is opened synchronously so popup blockers treat it as user-initiated.
	async function handleDownload() {
		if (downloadingRef.current) return;
		downloadingRef.current = true;
		setDownloading(true);
		setDownloadError(null);
		const tab = window.open('', '_blank');
		if (tab) tab.opener = null;
		try {
			const resp = await remote.get(`documents/${video.url}`);
			if (!resp?.url) throw new Error('No download url');
			if (tab) {
				tab.location.href = resp.url;
			} else {
				window.open(resp.url, '_blank', 'noopener');
			}
		} catch (e) {
			if (tab) tab.close();
			setDownloadError('There was an error downloading this guide');
		} finally {
			downloadingRef.current = false;
			setDownloading(false);
		}
	}

	return (
		<Page noBanner>
			<PageContentBodyGrid id="error-message" style={{ gridTemplateRows: "1fr" }}>
				<PageGridItem style={{ height: "100%" }} colSpan="12">
					<div className='mb-xl' style={{ maxWidth: 800 }}>
						<h5 className="mb-m">How to Guide</h5>
						<h2 className="mb-m">{video?.title || ''}</h2>
					</div>
					<Divider style={{ maxWidth: 800 }} />
					<div className="max-w-4xl mx-auto p-6 bg-white mt-xl">
						{/* Header */}
						{/* {videoTitle && (
							<div className="mb-6">
								<h2 className="header-1">{videoTitle}</h1>
							</div>
						)} */}

						{/* PDF download (PDF guides) or Video Player */}
						{pdfGuide
							? <FlexWrapper align="start" justify="center" direction="column" fullWidth style={{ maxWidth: '800px' }}>
								<p className="mb-m">Download the full guide as a PDF.</p>
								<Button icon={<Download />} disabled={downloading} loading={downloading} onClick={handleDownload}>Download PDF</Button>
								{downloadError && <p className="p-message mt-m">{downloadError}</p>}
							</FlexWrapper>
							: videoError
							? <FlexWrapper align="center" justify="center" direction="column" fullWidth style={{ height: "100%" }}>
								<h1 className='mb-m'>Video Error</h1>
								<p className='p-message'>There was an error loading this video</p>
							</FlexWrapper>
							: <div >
								<FlexWrapper align="start" justify="center" direction="column" fullWidth >
									{(loadingS3Video) ? (
										<div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
											<LoadingIcon />
										</div>
									)
										: <video
											ref={videoRef}
											src={s3VideoUrl}
											width="800"
											height="450"
											className="w-full"
											controls
											controlsList="nodownload"
											onPlay={() => setIsPlaying(true)}
											onPause={() => setIsPlaying(false)}
											onLoadStart={() => setVideoLoading(true)}
											onLoadedData={() => setVideoLoading(false)}
											onError={() => {
												setVideoError('Error loading video');
												setVideoLoading(false);
											}}
											style={{ background: 'black' }}
										/>
									}
								</FlexWrapper>
							</div>
						}

						{/* Transcription */}
						<FlexWrapper align="start" justify="center" direction="column" fullWidth style={{ height: "100%", maxWidth: '800px' }}>
							<Divider spacing="xl" />
							{video?.transcripts && video.transcripts.length > 0 ? (
								<div className="">
									<h2 className="header-2 mb-m">{pdfGuide ? 'Guide' : 'Transcription'}</h2>
									<p>{video?.description || ''}</p>

									{video.transcripts.map((step, index) => {
										// Sectioned titles ("Section | Step") show the section heading once, steps nested under it
										const { section, title } = splitStepTitle(step.title);
										const prevSection = index > 0 ? splitStepTitle(video.transcripts[index - 1].title).section : null;
										const continuesSection = section && section === prevSection;
										return (
										<div key={index} className="mb-xl mt-xl">
											{!continuesSection && <Divider spacingBottom="xl" />}
											{section && !continuesSection && (
												<h2 id={"section-header-" + index} className="header-2 mb-m">
													{section}
												</h2>
											)}
											{title && (
												<h3 id={"step-header-" + index} className={section ? "header-3 mb-m" : "header-2 mb-m"}>
													{title}
												</h3>
											)}
											{parseStepText(step.text).map((block, blockIndex) => {
												if (block.type === 'p') {
													return <p key={blockIndex} className="mb-m">{renderMarkdownText(block.text)}</p>;
												}
												const ListTag = block.type;
												return (
													<ListTag key={blockIndex} className="mb-m" style={{ paddingLeft: 24, listStyleType: block.type === 'ol' ? 'decimal' : 'disc' }}>
														{block.items.map((item, itemIndex) => <li key={itemIndex}>{renderMarkdownText(item)}</li>)}
													</ListTag>
												);
											})}
											{!loadingImages && images && images.length >= index + 1 && images[index] && (
												<img
													style={{
														maxWidth: '100%',
														maxHeight: '400px',
														borderStyle: 'solid',
														borderRadius: '4px',
														borderWidth: '1px',
														borderColor: '#E7EAF2'
													}}
													src={images[index]}
													alt={title || `Step ${index + 1}`}
													className=""
												/>
											)}
										</div>
										);
									})}
								</div>
							) : (
								<div className="text-center text-gray-500 py-8">
									<p>{pdfGuide ? 'No guide steps available' : 'No transcription available'}</p>
								</div>
							)}
						</FlexWrapper>
					</div>
				</PageGridItem>
			</PageContentBodyGrid>
		</Page >
	);
};

export default HelpPage;